import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';

import { userTypeAtom } from '../auth/state';
import { logger } from '../datadog';
import { useWindowFocusEffect } from '../hooks/useWindowFocusEffect';
import { useProviderLoginCallback } from '../provider/auth/useProviderLoginCallback';
import { useGetCurrentProviderQueryCallbackWithRefetch } from '../provider/useGetCurrentProviderQuery';
import { ModifiedToastAria } from '../toasts/types';
import { UserType } from '../types';
import { roomAtom } from '../video-sdk/core/atoms';
import { useClientLogin } from '../visitor/introduction-room/auth/useClientLogin';
import { useGetCurrentPatientQueryCallbackWithRefetch } from '../visitor/useGetCurrentPatientQuery';
import { SessionTimeoutToastBase } from './SessionTimeoutToast';
import type { SessionTimoutToastCommonProps } from './SessionTimeoutToast';
import { getLoginOptions, hideShownSessionTimeoutToast, showSessionRestoredToast } from './utils';

function useRefetchOnRefocus(refetchAndValidate: () => Promise<boolean>) {
  const t = useTranslate();
  const refetchOnRefocus = useCallback(
    async (isFocused: boolean) => {
      if (!isFocused) return;
      if (await refetchAndValidate()) {
        hideShownSessionTimeoutToast();
        showSessionRestoredToast(t);
      }
    },
    [refetchAndValidate, t],
  );

  useWindowFocusEffect(refetchOnRefocus);
}

function ClientSessionTimeoutToast({ isInSession, ...props }: SessionTimoutToastCommonProps) {
  const { patientShortId = '' } = useParams<{ patientShortId: string }>();
  const [getCurrentPatient] = useGetCurrentPatientQueryCallbackWithRefetch();
  const { handleLogin } = useClientLogin();
  const onPressReLogin = useCallback(() => {
    handleLogin(getLoginOptions(isInSession));
  }, [handleLogin, isInSession]);

  const refetchAndValidate = useCallback(async (): Promise<boolean> => {
    if (!patientShortId) {
      logger.error('Tried to refetch current patient without a patient short ID');
      return false;
    }

    const { data } = await getCurrentPatient({ variables: { patientShortId } });
    return !!data?.patientInfo;
  }, [getCurrentPatient, patientShortId]);

  useRefetchOnRefocus(refetchAndValidate);

  return <SessionTimeoutToastBase isInSession={isInSession} onPressReLogin={onPressReLogin} {...props} />;
}

function ProviderSessionTimeoutToast({ isInSession, ...props }: SessionTimoutToastCommonProps) {
  const [getCurrentProvider] = useGetCurrentProviderQueryCallbackWithRefetch();
  const handleLogin = useProviderLoginCallback();

  const onPressReLogin = useCallback(() => {
    handleLogin(getLoginOptions(isInSession));
  }, [handleLogin, isInSession]);

  const refetchAndValidate = useCallback(async (): Promise<boolean> => {
    const { data } = await getCurrentProvider();
    return !!data?.currentProvider;
  }, [getCurrentProvider]);

  useRefetchOnRefocus(refetchAndValidate);

  return <SessionTimeoutToastBase isInSession={isInSession} onPressReLogin={onPressReLogin} {...props} />;
}

/**
 * Component for displaying a login session timeout toast to the user. Supplies
 * a CTA to log in again appropriate for the current user type. Validates the
 * login session is successfully restored on window refocus and dismisses the
 * toast + shows a success toast if so.
 */
export function SessionTimeoutToast(props: ModifiedToastAria) {
  const isInSession = !!useAtomValue(roomAtom);

  const userType = useAtomValue(userTypeAtom);
  if (!userType) {
    logger.error('Tried to show session timeout toast without a user type');
    return;
  }

  const ToastComponent = userType === UserType.PROVIDER ? ProviderSessionTimeoutToast : ClientSessionTimeoutToast;

  return <ToastComponent isInSession={isInSession} {...props} />;
}
